import { useState, useEffect, useCallback } from 'react';
import { dashboardApi } from '../api/dashboardApi';
import {
  SuperAdminDashboardData,
  PMODashboardData,
  ManagerDashboardData,
  EmployeeDashboardData,
  UserRole,
} from '../types';

type DashboardData =
  | SuperAdminDashboardData
  | PMODashboardData
  | ManagerDashboardData
  | EmployeeDashboardData;

interface UseDashboardResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching dashboard data based on user role
 */
export function useDashboard<T extends DashboardData>(
  role: UserRole
): UseDashboardResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let result: DashboardData;

      switch (role) {
        case UserRole.SUPER_ADMIN:
          result = await dashboardApi.getSuperAdminDashboard();
          break;
        case UserRole.PMO:
          result = await dashboardApi.getPMODashboard();
          break;
        case UserRole.MANAGER:
          result = await dashboardApi.getManagerDashboard();
          break;
        case UserRole.EMPLOYEE:
          result = await dashboardApi.getEmployeeDashboard();
          break;
        default:
          throw new Error('Invalid role');
      }

      setData(result as T);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, isLoading, error, refetch: fetchDashboard };
}

// Specific hooks for each role (type-safe convenience hooks)
export function useSuperAdminDashboard() {
  return useDashboard<SuperAdminDashboardData>(UserRole.SUPER_ADMIN);
}

export function usePMODashboard() {
  return useDashboard<PMODashboardData>(UserRole.PMO);
}

export function useManagerDashboard() {
  return useDashboard<ManagerDashboardData>(UserRole.MANAGER);
}

export function useEmployeeDashboard() {
  return useDashboard<EmployeeDashboardData>(UserRole.EMPLOYEE);
}

export default useDashboard;
